<script setup lang="ts">
import { ArrowLeft, Menu, Search, Settings, X } from 'lucide-vue-next'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Input } from '@/components/ui/input'
import type { AppNavGroup, AppNavItem } from '~/composables/useAppNavigation'
import type { MobilePageMenuItem, MobilePageMenuSection } from '~/composables/useMobilePageMenu'

type QuickActionItem = {
  id: string
  label: string
  icon?: any
  action: () => void | Promise<void>
  badge?: string | number
  active?: boolean
}

const props = withDefaults(defineProps<{
  modelValue: boolean
  workspaceName: string
  workspaceMeta: string
  navGroups: AppNavGroup[]
  settingsNavGroups?: AppNavGroup[]
  accountName?: string
  accountMeta?: string
  accountAvatarUrl?: string
  accountInitials?: string
  accountItems?: AppNavItem[]
  pageSections?: MobilePageMenuSection[]
  pageActions?: MobilePageMenuItem[]
  quickActions: QuickActionItem[]
  quickActionLoading?: boolean
  isItemActive: (groupLabel: string, url: string) => boolean
}>(), {
  accountName: '',
  accountMeta: '',
  accountAvatarUrl: '',
  accountInitials: '',
  accountItems: () => [],
  settingsNavGroups: () => [],
  pageSections: () => [],
  pageActions: () => [],
  quickActionLoading: false,
})

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const route = useRoute()
const isSettingsPath = (path: string) => path.includes('/settings') || path.includes('/account')
const open = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})
const quickActionsScroller = ref<HTMLElement | null>(null)
const quickActionsScrollLeft = ref(0)
const navSearchQuery = ref('')
const navView = ref<'workspace' | 'settings'>(
  isSettingsPath(route.path)
    ? 'settings'
    : 'workspace',
)
const activeNavGroups = computed(() => (
  navView.value === 'settings' ? props.settingsNavGroups : props.navGroups
))

const activeItemTitle = computed(() => {
  for (const group of [...props.navGroups, ...props.settingsNavGroups]) {
    for (const item of group.items) {
      if (item.url && props.isItemActive(group.label || 'Workspace', item.url)) {
        return item.title
      }
    }
  }

  return 'Navigate'
})

const visibleNavGroups = computed(() => {
  const query = navSearchQuery.value.trim().toLowerCase()
  if (!query) return activeNavGroups.value

  return activeNavGroups.value
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => (
                item.title.toLowerCase().includes(query)
        || String(group.label || 'workspace').toLowerCase().includes(query)
        || String(item.badge || '').toLowerCase().includes(query)
        || String(item.notificationCount || '').includes(query)
      )),
    }))
    .filter((group) => group.items.length > 0)
})

const drawerQuickActions = computed<QuickActionItem[]>(() => {
  const merged = [...props.pageActions, ...props.quickActions]
  const seen = new Set<string>()

  return merged.filter((item) => {
    if (seen.has(item.id)) return false
    seen.add(item.id)
    return true
  })
})

async function handleNavItemSelect(item: AppNavItem) {
  open.value = false

  if (item.url && !item.disabled) {
    await navigateTo(item.url)
    return
  }

  await item.action?.()
}

async function handleQuickActionSelect(item: QuickActionItem) {
  open.value = false
  await item.action()
}

async function handlePageMenuItemSelect(item: MobilePageMenuItem) {
  open.value = false
  await item.action()
}

async function handleAccountItemSelect(item: AppNavItem) {
  open.value = false

  if (item.url && !item.disabled) {
    await navigateTo(item.url)
    return
  }

  await item.action?.()
}

function handleQuickActionsScroll(event: Event) {
  const target = event.target as HTMLElement | null
  quickActionsScrollLeft.value = target?.scrollLeft || 0
}

function openSettingsNavigation() {
  navSearchQuery.value = ''
  navView.value = 'settings'
}

function openWorkspaceNavigation() {
  navSearchQuery.value = ''
  navView.value = 'workspace'
}

watch(open, async(value) => {
  if (!value) {
    navSearchQuery.value = ''
    return
  }

  await nextTick()

  if (quickActionsScroller.value) {
    quickActionsScroller.value.scrollLeft = quickActionsScrollLeft.value
  }
})

watch(
  () => route.path,
  (path) => {
    navView.value = isSettingsPath(path)
      ? 'settings'
      : 'workspace'
  },
)
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-180 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-140 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <button
        v-if="open"
        type="button"
        class="fixed inset-0 z-[58] bg-black/12 backdrop-blur-[2px] md:hidden"
        aria-label="Close navigation"
        @click="open = false"
      />
    </Transition>

    <Transition
      enter-active-class="transition duration-220 ease-out"
      enter-from-class="translate-y-4 opacity-0 scale-[0.98]"
      enter-to-class="translate-y-0 opacity-100 scale-100"
      leave-active-class="transition duration-160 ease-in"
      leave-from-class="translate-y-0 opacity-100 scale-100"
      leave-to-class="translate-y-3 opacity-0 scale-[0.99]"
    >
        <div
        v-if="open"
        class="fixed inset-x-3 bottom-[calc(env(safe-area-inset-bottom)+5.25rem)] z-[60] overflow-hidden rounded-[1.35rem] border border-border/70 bg-background shadow-[0_18px_48px_rgba(15,23,42,0.12)] md:hidden"
      >
        <div class="border-b border-border/60 px-4 pb-3 pt-3">
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <p class="text-[10px] font-semibold uppercase tracking-[0.24em] text-muted-foreground/75">{{ navView === 'settings' ? 'Settings' : 'Menu' }}</p>
              <p class="mt-1.5 truncate text-base font-semibold tracking-tight text-foreground">{{ workspaceName }}</p>
              <p class="truncate text-[13px] text-muted-foreground">{{ workspaceMeta }}</p>
            </div>
            <div class="rounded-full bg-foreground/6 px-2.5 py-1 text-[11px] font-medium text-foreground">
              {{ activeItemTitle }}
            </div>
          </div>

          <div class="relative mt-3">
            <Search class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              v-model="navSearchQuery"
              class="h-10 rounded-xl border-border/70 bg-background/75 pl-9"
              placeholder="Search pages..."
            />
          </div>
        </div>

        <div class="relative">
          <div class="max-h-[min(62svh,34rem)] overflow-y-auto px-4 pb-28 pt-2">
            <section
              v-if="navView === 'workspace' && pageSections.length > 0"
              class="border-b border-border/60 py-3"
            >
              <p class="px-1 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground/70">On this page</p>
              <div class="space-y-3">
                <div
                  v-for="section in pageSections"
                  :key="section.id"
                  class="space-y-0.5"
                >
                    <p class="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground/60">{{ section.label }}</p>
                  <button
                    v-for="item in section.items"
                    :key="item.id"
                    type="button"
                    class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left transition-all"
                    :class="item.active
                      ? 'bg-[color:color-mix(in_srgb,var(--brand-primary)_10%,var(--background))] text-foreground'
                      : 'text-foreground/88 hover:bg-foreground/5 hover:text-foreground'"
                    @click="handlePageMenuItemSelect(item)"
                  >
                    <span
                      v-if="item.icon"
                      class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                      :class="item.active
                        ? 'bg-[color:color-mix(in_srgb,var(--brand-primary)_14%,var(--background))]'
                        : 'bg-foreground/6'"
                    >
                      <component :is="item.icon" class="h-4 w-4" />
                    </span>

                    <span class="min-w-0 flex-1 truncate text-[13px] font-medium">{{ item.label }}</span>

                    <span
                      v-if="item.badge"
                      class="shrink-0 rounded-full bg-foreground/6 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
                    >
                      {{ item.badge }}
                    </span>
                  </button>
                </div>
              </div>
            </section>

            <section
              v-if="navView === 'workspace' && pageActions.length > 0"
              class="border-b border-border/60 py-3"
            >
              <p class="px-1 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground/70">Page actions</p>
              <div class="space-y-0.5">
                <button
                  v-for="item in pageActions"
                  :key="item.id"
                  type="button"
                  class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left text-foreground/88 transition-all hover:bg-foreground/5 hover:text-foreground"
                  @click="handlePageMenuItemSelect(item)"
                >
                  <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-foreground/6">
                    <component :is="item.icon" class="h-4 w-4" />
                  </span>
                  <span class="min-w-0 flex-1 truncate text-[13px] font-medium">{{ item.label }}</span>
                  <span
                    v-if="item.notificationCount || item.badge"
                    class="shrink-0 rounded-full bg-foreground/6 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    {{ item.notificationCount || item.badge }}
                  </span>
                </button>
              </div>
            </section>

            <section
              v-for="group in visibleNavGroups"
              :key="group.id"
              class="border-b border-border/60 py-3 last:border-b-0"
            >
              <p class="px-1 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground/70">{{ group.label || 'Workspace' }}</p>
              <div class="space-y-0.5">
                <button
                  v-for="item in group.items"
                  :key="`${group.id}-${item.title}`"
                  type="button"
                  class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left transition-all"
                  :class="props.isItemActive(group.label || 'Workspace', item.url || '')
                    ? 'bg-muted text-foreground'
                    : 'text-foreground/88 hover:bg-foreground/5 hover:text-foreground'"
                  :disabled="item.disabled && !item.action"
                  @click="handleNavItemSelect(item)"
                >
                  <span
                    class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                    :class="props.isItemActive(group.label || 'Workspace', item.url || '')
                      ? 'bg-background'
                      : 'bg-foreground/6'"
                  >
                    <component :is="item.icon" class="h-4 w-4" />
                  </span>

                  <span class="min-w-0 flex-1 truncate text-[13px] font-medium">{{ item.title }}</span>

                  <span
                    v-if="item.badge"
                    class="shrink-0 rounded-full bg-foreground/6 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    {{ item.badge }}
                  </span>
                </button>
              </div>
            </section>

            <section
              v-if="visibleNavGroups.length === 0"
              class="py-6"
            >
              <div class="rounded-2xl border border-dashed border-border/70 px-4 py-6 text-center">
                <p class="text-sm font-medium text-foreground">No matching pages</p>
                <p class="mt-1 text-xs text-muted-foreground">Try searching by page name or section.</p>
              </div>
            </section>

            <section
              v-if="navView === 'workspace' && accountItems.length > 0"
              class="border-b border-border/60 py-3"
            >
              <div class="px-1 pb-2">
                <div class="flex items-center gap-3 rounded-2xl border border-border/70 bg-foreground/[0.03] px-3 py-3">
                  <Avatar class="h-10 w-10 rounded-xl">
                    <AvatarImage :src="accountAvatarUrl || ''" :alt="accountName || 'Account'" />
                    <AvatarFallback class="rounded-xl bg-primary/12 text-xs font-semibold text-primary">
                      {{ accountInitials || 'VU' }}
                    </AvatarFallback>
                  </Avatar>
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-sm font-semibold text-foreground">{{ accountName || 'Account' }}</p>
                    <p class="truncate text-xs text-muted-foreground">{{ accountMeta }}</p>
                  </div>
                </div>
              </div>

              <p class="px-1 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground/70">Account</p>
              <div class="space-y-0.5">
                <button
                  v-for="item in accountItems"
                  :key="`account-${item.title}`"
                  type="button"
                  class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left text-foreground/88 transition-all hover:bg-foreground/5 hover:text-foreground"
                  :disabled="item.disabled && !item.action"
                  @click="handleAccountItemSelect(item)"
                >
                  <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-foreground/6">
                    <component :is="item.icon" class="h-4 w-4" />
                  </span>
                  <span class="min-w-0 flex-1 truncate text-[13px] font-medium">{{ item.title }}</span>
                  <span
                    v-if="item.badge"
                    class="shrink-0 rounded-full bg-foreground/6 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground"
                  >
                    {{ item.badge }}
                  </span>
                </button>
              </div>
            </section>

            <section class="py-3">
              <button
                type="button"
                class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left text-foreground/88 transition-colors hover:bg-foreground/5 hover:text-foreground"
                @click="navView === 'settings' ? openWorkspaceNavigation() : openSettingsNavigation()"
              >
                <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-foreground/6">
                  <component :is="navView === 'settings' ? ArrowLeft : Settings" class="h-4 w-4" />
                </span>
                <span class="min-w-0 flex-1 truncate text-[13px] font-medium">
                  {{ navView === 'settings' ? 'Back to workspace' : 'Settings' }}
                </span>
              </button>
            </section>
          </div>

          <div
            v-if="navView === 'workspace' && drawerQuickActions.length > 0"
            class="absolute inset-x-0 bottom-0 border-t border-border/60 bg-[color:color-mix(in_srgb,var(--background)_90%,transparent)] px-4 pb-3 pt-3 backdrop-blur-xl"
          >
            <div class="pointer-events-none absolute inset-x-0 -top-10 h-10 bg-gradient-to-t from-[color:color-mix(in_srgb,var(--background)_96%,transparent)] via-[color:color-mix(in_srgb,var(--background)_76%,transparent)] to-transparent" />
            <div class="flex items-center justify-between gap-3">
              <p class="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground/70">{{ pageActions.length > 0 ? 'Page & quick actions' : 'Quick actions' }}</p>
              <span class="text-[11px] text-muted-foreground">Swipe for more</span>
            </div>
            <div class="relative mt-2">
              <div class="pointer-events-none absolute inset-y-0 left-0 z-10 w-5 bg-gradient-to-r from-[color:color-mix(in_srgb,var(--background)_94%,transparent)] to-transparent" />
              <div class="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-[color:color-mix(in_srgb,var(--background)_94%,transparent)] via-[color:color-mix(in_srgb,var(--background)_72%,transparent)] to-transparent" />
              <div
                ref="quickActionsScroller"
                class="no-scrollbar flex gap-2 overflow-x-auto pb-1 pr-4"
                @scroll.passive="handleQuickActionsScroll"
              >
              <button
                v-for="item in drawerQuickActions"
                :key="item.id"
                type="button"
                class="flex min-w-[7.25rem] shrink-0 items-center gap-2 rounded-xl bg-foreground/6 px-3 py-2.5 text-left text-foreground transition-all hover:bg-foreground/9"
                :disabled="quickActionLoading"
                @click="handleQuickActionSelect(item)"
              >
                <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-background/80">
                  <component :is="item.icon || Menu" class="h-3.5 w-3.5" />
                </span>
                <span class="min-w-0 truncate text-[12px] font-medium">{{ item.label }}</span>
              </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <div aria-hidden="true" class="mobile-nav-safe-fade" />

    <div class="pointer-events-none fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+1rem)] z-[70] flex justify-center md:hidden">
      <button
        type="button"
        data-tour="workspace-mobile-navigation"
        class="pointer-events-auto flex min-w-[11rem] items-center gap-2.5 rounded-[1.2rem] border border-border/70 bg-[color:color-mix(in_srgb,var(--background)_82%,var(--card))] px-3.5 py-2.5 text-left shadow-[0_18px_40px_rgba(15,23,42,0.22)] backdrop-blur-xl transition-all duration-200 hover:-translate-y-0.5"
        :class="open ? 'scale-[0.98] shadow-[0_14px_28px_rgba(15,23,42,0.18)]' : 'scale-100'"
        @click="open = !open"
      >
        <span class="flex items-center gap-2.5">
          <span
            class="flex h-8 w-8 items-center justify-center rounded-lg text-foreground transition-all duration-200"
            :class="open ? 'bg-[color:color-mix(in_srgb,var(--brand-primary)_14%,var(--background))]' : 'bg-foreground/6'"
          >
            <component :is="open ? X : Menu" class="h-4 w-4 transition-transform duration-200" :class="open ? 'rotate-90 scale-95' : 'rotate-0 scale-100'" />
          </span>
          <span class="min-w-0">
            <span class="block text-[13px] font-semibold text-foreground">{{ open ? 'Close' : 'Menu' }}</span>
            <span class="block truncate text-[11px] text-muted-foreground">{{ open ? 'Back to workspace' : activeItemTitle }}</span>
          </span>
        </span>
      </button>
    </div>
  </Teleport>
</template>
